// ABOUTME: Modal to split the weight of a return over its parcels, one weight in kg per shipment.
// ABOUTME: Calculates the return costs with those weights; each parcel is rated on its own weight.
import { Box, InputBase, useTheme } from "@mui/material";
import { useState, useEffect } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useCalculateReturnCostsMutation } from "../../../newapi/financial/invoiceSlice";
import { showToastMessage, showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../utils/toasts";

const MSplitReturnWeight = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Weight in kg as typed, per shipment id
    const [weights, setWeights] = useState({});

    const shipments = props.returnData?.shipments ?? [];

    const returnWeight = props.returnData?.returnWeight;
    const returnGrams = returnWeight !== null && returnWeight !== undefined ? parseInt(returnWeight) : 0;

    // Opens with the return weight split evenly over the parcels; the remaining grams go to the
    // last parcel so the parts add up to the return weight. The fields stay editable.
    // Without a known return weight the fields start empty.
    useEffect(() => {
        if (!props.open) return;

        const split = {};
        if (returnGrams > 0 && shipments.length > 0) {
            const part = Math.floor(returnGrams / shipments.length);
            shipments.forEach((shipment, index) => {
                const grams = index === shipments.length - 1 ? returnGrams - part * (shipments.length - 1) : part;
                split[shipment.id] = String(grams / 1000);
            });
        }
        setWeights(split);
    }, [props.open, props.returnData]);

    const handleChange = (shipmentId, value) => {
        if (!/^\d*[.,]?\d*$/.test(value)) {
            return;
        }
        setWeights(prev => ({ ...prev, [shipmentId]: value }));
    };

    const toGrams = (value) => Math.round(parseFloat(String(value ?? "").replace(",", ".")) * 1000);

    const enteredGrams = shipments.reduce((total, shipment) => total + (toGrams(weights[shipment.id]) || 0), 0);

    const [calculateReturnCosts, {isLoading}] = useCalculateReturnCostsMutation();
    const handleSave = async () => {
        try {
            if (isLoading) return;

            const shipmentWeights = {};
            for (const shipment of shipments) {
                const grams = toGrams(weights[shipment.id]);
                if (!(grams > 0)) {
                    showToastMessage("error", t("split_return_weight_required"));
                    return;
                }
                shipmentWeights[shipment.id] = grams;
            }

            const response = await calculateReturnCosts({
                returnIds: [props.returnData.id],
                shipmentWeights: shipmentWeights
            }).unwrap();

            // Not every parcel got a rate: nothing is booked, the modal stays open to adjust the weights
            if (response.notCalculatedReturnIds?.includes(props.returnData.id)) {
                showToastMessage("warning", t("split_return_weight_no_rate"));
                return;
            }

            showToastMessageUpdateRequest(t, response);
            props.handleClose();
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return(
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("split_return_weight")} {props.returnData?.returnReference}</Subheading>
                <Text light>{t("split_return_weight_subtitle")}</Text>
                <Text light>
                    {t("split_return_weight_totals", {
                        returnWeight: returnWeight !== null && returnWeight !== undefined ? parseFloat(parseInt(returnWeight) / 1000) : "-",
                        enteredWeight: parseFloat(enteredGrams / 1000)
                    })}
                </Text>
            </Box>

            {/* BODY */}
            <Box sx={{maxHeight: "65vh", overflow: "auto", background: colors.white[400]}}>
                {shipments.map((shipment) => (
                    <Box key={"split-return-weight-"+shipment.id}
                        sx={{
                            background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            paddingRight:3,
                            paddingLeft:3,
                            paddingTop:1,
                            paddingBottom:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:1
                        }}
                    >
                        <Text>{shipment.trackingNumber || shipment.id}</Text>

                        <Box display="flex"
                            height="40px"
                            width="160px"
                            borderRadius="20px"
                            alignItems="center"
                            backgroundColor={colors.grey[100]}
                        >
                            <InputBase
                                sx={{
                                    px:2,
                                    flex:1,
                                    color:colors.txt["primary"],
                                    lineHeight:"32px",
                                }}
                                value={weights[shipment.id] ?? ""}
                                onChange={(e) => handleChange(shipment.id, e.target.value)}
                            />
                            <Box pr={2}><Text light>kg</Text></Box>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box
                component="footer"
                sx={{
                    pt:2,
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"}
                    color={"green"}
                    variant="contained"
                    onClick={handleSave}
                    >{t("recalculate_invoice_product")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MSplitReturnWeight;
